import ProtectedRoute from './ProtectedRoute'
import PublicRoute from './PublicRoute'
import AuthorityGuard from './AuthorityGuard'
import AppRoute from './AppRoute'
import PageContainer from '@/components/template/PageContainer'
import { protectedRoutes, publicRoutes, openRoutes, helpRoutes } from '@/configs/routes.config'
import { useAuth } from '@/auth'
import { Routes, Route, Navigate } from 'react-router'
import DocumentWorkspaceLayout from '@/pages/DocumentWorkspaceLayout'
import { lazyWithRetry } from '@/utils/lazyWithRetry'

const HomePage = lazyWithRetry(() => import('@/pages/HomePage'))
import type { LayoutType } from '@/@types/theme'

/** Single workspace route /d/:id (the editor). */
const WORKSPACE_ROUTE_KEYS = new Set(['editor'])

interface ViewsProps {
    pageContainerType?: 'default' | 'gutterless' | 'contained'
    layout?: LayoutType
}

type AllRoutesProps = ViewsProps

const AllRoutes = (props: AllRoutesProps) => {
    const { user, authenticated } = useAuth()

    return (
        <Routes>
            {/* Open routes — accessible regardless of auth state */}
            {openRoutes.map((route) => (
                <Route
                    key={route.path}
                    path={route.path}
                    element={
                        <AppRoute
                            routeKey={route.key}
                            component={route.component}
                            {...route.meta}
                        />
                    }
                />
            ))}
            {/* Help — in the app's frame when signed in, on a plain page when not. */}
            {helpRoutes.map((route) => (
                <Route
                    key={route.path}
                    path={route.path}
                    element={
                        authenticated ? (
                            <PageContainer {...props} {...route.meta}>
                                <AppRoute routeKey={route.key} component={route.component} {...route.meta} />
                            </PageContainer>
                        ) : (
                            <AppRoute routeKey={route.key} component={route.component} layout="blank" />
                        )
                    }
                />
            ))}
            {/* No index here: `/` is Home, behind ProtectedRoute below, which
                sends a signed-out visitor to sign-in. An index under
                PublicRoute would send a signed-in one to the entry path — `/`
                itself — and loop. */}
            <Route path="/" element={<PublicRoute />}>
                {publicRoutes.map((route) => (
                    <Route
                        key={route.path}
                        path={route.path}
                        element={
                            <AppRoute
                                routeKey={route.key}
                                component={route.component}
                                {...route.meta}
                            />
                        }
                    />
                ))}
            </Route>
            <Route path="/" element={<ProtectedRoute />}>
                {/* Home — where the app opens (navigation handoff §3). */}
                <Route
                    index
                    element={
                        <PageContainer {...props} pageContainerType="gutterless" pageBackgroundType="plain">
                            <AppRoute routeKey="home" component={HomePage} />
                        </PageContainer>
                    }
                />
                {protectedRoutes
                    .filter((route) => !WORKSPACE_ROUTE_KEYS.has(route.key))
                    .map((route, index) => (
                    <Route
                        key={route.key + index}
                        path={route.path}
                        element={
                            <AuthorityGuard
                                userAuthority={user.authority}
                                authority={route.authority}
                            >
                                <PageContainer {...props} {...route.meta}>
                                    <AppRoute
                                        routeKey={route.key}
                                        component={route.component}
                                        {...route.meta}
                                    />
                                </PageContainer>
                            </AuthorityGuard>
                        }
                    />
                ))}
                {/* /d/:id — the editor workspace. The mode was once a query param
                    so that switching it never remounted the route. */}
                {protectedRoutes
                    .filter((route) => WORKSPACE_ROUTE_KEYS.has(route.key))
                    .map((route) => (
                        <Route
                            key={route.key}
                            path={route.path}
                            element={
                                <AuthorityGuard userAuthority={user.authority} authority={route.authority}>
                                    <PageContainer
                                        {...props}
                                        pageContainerType="gutterless"
                                        footer={false}
                                        layout="blank"
                                    >
                                        <DocumentWorkspaceLayout />
                                    </PageContainer>
                                </AuthorityGuard>
                            }
                        />
                    ))}
                <Route path="*" element={<Navigate replace to="/" />} />
            </Route>
        </Routes>
    )
}

export default AllRoutes
